import { clsx, type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"

/**
 * tailwind-merge must know the design system's own scales (styles/theme.css).
 * Out of the box it reads an unknown `text-callout` as a text COLOUR, so
 * `cn("text-callout text-muted-foreground")` returned only the colour — every
 * v6 type role written next to a colour inside cn() silently vanished, and no
 * jsdom test could see it (there is no computed font size there). Same for the
 * spacing keys (`p-page` vs `p-4` did not dedupe) and the motion/z utilities.
 * utilsCn guard: src/test/v6Primitives.test.tsx › "cn keeps the v6 scales".
 */
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      text: ["display", "title-1", "title-2", "title-3", "body", "callout", "footnote", "caption"],
      spacing: ["page", "section", "stack", "inline", "panel", "control", "cell", "tile", "tile-compact"],
      shadow: ["soft-xs", "soft-sm", "soft-md", "soft-lg", "soft-xl"],
      ease: ["standard", "emphasized", "spring"],
    },
    classGroups: {
      duration: [{ duration: ["xs", "sm", "md", "lg", "xl"] }],
      z: [{ z: ["sticky", "toolbar", "peek", "shell", "overlay", "toast"] }],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Human-readable byte size (e.g. 1536 → "1.5 KB"). Used for tenant data-usage
// figures in the super-admin views.
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return "0 B"
  const units = ["B", "KB", "MB", "GB", "TB"]
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
  const value = bytes / 1024 ** i
  return `${i === 0 ? value : value.toFixed(value < 10 ? 1 : 0)} ${units[i]}`
}
